import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { NumberFormatter, Pagination, Skeleton, Table } from '@mantine/core';
import dayjs from 'dayjs';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import {
  OFFLINE_PAYMENT_TYPES,
  PAYMENT_TYPE_ENUM,
  REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES
} from '../../../config/constants';
import { formatUtcDate } from '../../../helpers/format';

const RegCartOrderTransactionPaymentTable = ({ loading, regCartOrder }) => {
  const MAX_PAGE_LENGTH = 5;
  const [pageIndex, setPageIndex] = useState(1);

  const reimbursementTransactions = regCartOrder?.ecomOrder
    ? regCartOrder.ecomOrder.ecomOrderPayments
        .filter(
          (p) =>
            p.fkRegFormSubmissionPaymentType.toString() ===
            PAYMENT_TYPE_ENUM.REIMBURSEMENT
        )
        .map((p) => ({
          entityId: p.pkEcomOrderPayment,
          paymentType: PAYMENT_TYPE_ENUM.REIMBURSEMENT,
          createdAt: p.createdAt,
          sourceTransaction: p.sourceTransaction,
          amountInCents: p.amount
        }))
    : [];

  const invoices =
    regCartOrder?.cart?.lineItems.flatMap((li) => li.invoices) || [];

  const allTransactions = regCartOrder
    ? [
        ...regCartOrder.cart.payments,
        ...regCartOrder.cart.refunds,
        ...regCartOrder.cart.forgiveness,
        ...reimbursementTransactions,
        ...invoices.map((i) => ({
          entityId: i.entityId,
          paymentType: PAYMENT_TYPE_ENUM.INVOICE,
          createdAt: i.createdAt,
          sourceTransaction: i.invoiceId,
          invoiceId: i.invoiceId,
          amountInCents: i.totalInCents,
          isInvoice: true,
          invoiceStatus: i.invoiceStatus,
          isInvoiceRefunded: i.refundedAmountInCents > 0
        })),
        ...invoices.flatMap((i) => i.payments),
        ...invoices.flatMap((i) => i.refunds)
      ]
    : [];

  const transactionsGroupedBySource = allTransactions.reduce((r, c) => {
    const existingTransaction = r.find(
      (t) => t.sourceTransaction === c.sourceTransaction
    );
    if (existingTransaction) {
      existingTransaction.amountInCents += c.amountInCents;
      existingTransaction.payments.push(c);
    }
    else {
      r.push({
        entityId: c.entityId,
        sourceTransaction: c.sourceTransaction,
        paymentType: c.paymentType.toString(),
        createdAt: c.createdAt,
        amountInCents: c.amountInCents,
        isInvoice: c.isInvoice,
        invoiceStatus: c.invoiceStatus,
        invoiceId: c.invoiceId,
        isInvoiceRefunded: c.isInvoiceRefunded,
        payments: [c]
      });
    }
    return r;
  }, []);

  const paidOnline = transactionsGroupedBySource.some(
    (p) =>
      !p.isInvoice &&
      (p.paymentType === PAYMENT_TYPE_ENUM.STRIPE ||
        p.paymentType === PAYMENT_TYPE_ENUM.AFTERPAY ||
        p.paymentType === PAYMENT_TYPE_ENUM.SPORTSPAY ||
        p.paymentType === PAYMENT_TYPE_ENUM.SPORTSPAY_INSTALLMENTS)
  );

  return (
    <AppStack>
      <AppStack style={{ overflow: 'auto', flex: 1, gap: 0 }}>
        <Table
          horizontalSpacing="md"
          style={{ border: 'solid 1px #ced4da' }}
          verticalSpacing="md"
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  STATUS
                </AppText>
              </Table.Th>
              <Table.Th>
                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  DATE
                </AppText>
              </Table.Th>
              <Table.Th>
                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  TRANSACTION ID
                </AppText>
              </Table.Th>
              <Table.Th>
                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  TYPE
                </AppText>
              </Table.Th>
              <Table.Th>
                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  DESCRIPTION
                </AppText>
              </Table.Th>
              <Table.Th style={{ borderRight: 'solid 1px #ced4da' }}>
                <AppText
                  style={{
                    whiteSpace: 'nowrap',
                    textAlign: 'end',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  GROSS AMOUNT
                </AppText>
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {loading ? (
              <Table.Tr>
                <Table.Th>
                  <Skeleton height={18} width={80} />
                </Table.Th>
                <Table.Th>
                  <Skeleton height={18} width={200} />
                </Table.Th>
                <Table.Th>
                  <Skeleton height={18} width={200} />
                </Table.Th>
                <Table.Th>
                  <Skeleton height={18} width={200} />
                </Table.Th>
                <Table.Th>
                  <Skeleton height={18} width={200} />
                </Table.Th>
                <Table.Th style={{ borderRight: 'solid 1px #ced4da' }}>
                  <Skeleton
                    height={18}
                    style={{ marginLeft: 'auto' }}
                    width={80}
                  />
                </Table.Th>
              </Table.Tr>
            ) : transactionsGroupedBySource.length === 0 ? (
              <Table.Tr>
                <Table.Td
                  colSpan="6"
                  style={{ padding: 50, textAlign: 'center' }}
                >
                  <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                    - No Payments Received -
                  </AppText>
                </Table.Td>
              </Table.Tr>
            ) : (
              transactionsGroupedBySource
                ?.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                .slice(
                  -MAX_PAGE_LENGTH + pageIndex * MAX_PAGE_LENGTH,
                  pageIndex * MAX_PAGE_LENGTH
                )
                .map((p) => {
                  const statusInfo = p.isInvoice
                    ? p.isInvoiceRefunded
                      ? {
                          color: '#c40000',
                          label: 'REFUNDED'
                        }
                      : p.invoiceStatus?.toString() ===
                        REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES.PAID
                      ? {
                          color: '#067D62',
                          label: 'PAID'
                        }
                      : p.invoiceStatus?.toString() ===
                        REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES.VOID
                      ? {
                          color: '#c40000',
                          label: 'VOID'
                        }
                      : p.invoiceStatus?.toString() ===
                        REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES.UNCOLLECTIBLE
                      ? {
                          color: 'orange',
                          label: 'UNCOLLECTIBLE'
                        }
                      : {
                          color: '#000',
                          label: 'Pending'
                        }
                    : p.paymentType === PAYMENT_TYPE_ENUM.REFUND
                    ? {
                        color: '#c40000',
                        label: 'REFUND'
                      }
                    : p.paymentType === PAYMENT_TYPE_ENUM.REIMBURSEMENT
                    ? {
                        color: 'dodgerblue',
                        label: 'REIMBURSEMENT'
                      }
                    : p.paymentType === PAYMENT_TYPE_ENUM.FORGIVENESS
                    ? {
                        color: 'orange',
                        label: 'FORGIVENESS'
                      }
                    : {
                        color: '#067D62',
                        label: 'PAID'
                      };

                  const paymentType = OFFLINE_PAYMENT_TYPES.find(
                    (t) => t.value === p.paymentType
                  );

                  return (
                    <Table.Tr key={p.entityId}>
                      <Table.Th>
                        <AppStack style={{ gap: 0 }}>
                          <AppText
                            style={{
                              whiteSpace: 'nowrap',
                              fontSize: 14,
                              color: statusInfo.color,
                              fontWeight: 650
                            }}
                          >
                            {statusInfo.label}
                          </AppText>
                        </AppStack>
                      </Table.Th>
                      <Table.Th>
                        <AppText
                          style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                        >
                          {dayjs(formatUtcDate(p.createdAt)).format(
                            'MMMM D, YYYY h:mm A'
                          )}
                        </AppText>
                      </Table.Th>
                      <Table.Th>
                        <AppText
                          style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                        >
                          {p.sourceTransaction}
                        </AppText>
                      </Table.Th>
                      <Table.Th>
                        <AppText
                          style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                        >
                          {paymentType.label}
                        </AppText>
                      </Table.Th>
                      <Table.Th>
                        <AppText
                          style={{ whiteSpace: 'nowrap', fontWeight: 'normal' }}
                        >
                          {(p.invoiceId && p.isInvoice) ||
                          (paymentType.value === PAYMENT_TYPE_ENUM.REFUND &&
                            paidOnline) ||
                          paymentType.value === PAYMENT_TYPE_ENUM.STRIPE ||
                          paymentType.value === PAYMENT_TYPE_ENUM.AFTERPAY ||
                          paymentType.value === PAYMENT_TYPE_ENUM.SPORTSPAY ||
                          paymentType.value ===
                            PAYMENT_TYPE_ENUM.SPORTSPAY_INSTALLMENTS
                            ? 'Card'
                            : paymentType.value === PAYMENT_TYPE_ENUM.JUMPSTART
                            ? 'Assistance'
                            : 'Offline'}
                        </AppText>
                      </Table.Th>
                      <Table.Th style={{ borderRight: 'solid 1px #ced4da' }}>
                        <AppText
                          style={{
                            whiteSpace: 'nowrap',
                            textAlign: 'end',
                            fontWeight: 'normal',
                            textDecoration:
                              p.isInvoice &&
                              p.invoiceStatus?.toString() ===
                                REG_FORM_SUBMISSION_INVOICE_STATUS_ENUM_VALUES.VOID
                                ? 'line-through'
                                : 'unset',
                            color:
                              p.isInvoice ||
                              p.paymentType ===
                                PAYMENT_TYPE_ENUM.REIMBURSEMENT ||
                              p.paymentType === PAYMENT_TYPE_ENUM.FORGIVENESS
                                ? '#000'
                                : statusInfo.color
                          }}
                        >
                          <NumberFormatter
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            style={{
                              fontSize: 14,
                              fontWeight: 650
                            }}
                            suffix=" CAD"
                            thousandSeparator
                            value={
                              p.paymentType === PAYMENT_TYPE_ENUM.REFUND
                                ? (p.amountInCents / 100) * -1
                                : p.amountInCents / 100
                            }
                          />
                        </AppText>
                      </Table.Th>
                    </Table.Tr>
                  );
                })
            )}
          </Table.Tbody>
        </Table>
      </AppStack>
      <AppFlexbox style={{ justifyContent: 'center' }}>
        <AppStack>
          {!loading && transactionsGroupedBySource.length > MAX_PAGE_LENGTH && (
            <Pagination
              color="blue"
              onChange={setPageIndex}
              total={Math.ceil(allTransactions.length / MAX_PAGE_LENGTH)}
              value={pageIndex}
            />
          )}
        </AppStack>
      </AppFlexbox>
    </AppStack>
  );
};

RegCartOrderTransactionPaymentTable.propTypes = {
  loading: PropTypes.bool,
  regCartOrder: PropTypes.object
};

export default RegCartOrderTransactionPaymentTable;
